import * as THREE from "three";
import gsap from "gsap";

import { createTextureFromWrapperAsync } from "@/scripts/utils/webgl/TextureFactory";
import { domRectToWorld } from "@/scripts/utils/webgl/DOMTracker";
import { createShaderMaterial, syncCursorWarp } from "@/scripts/utils/webgl/ShaderMaterial";
import { ShaderPlaneGroup, tvShader } from "@/scripts/utils/webgl/ShaderPlaneGroup";
import { loaderDone } from "@/scripts/base/loader";
import { prefersReducedMotion } from "@/scripts/utils/variables";

interface HeroPlane {
    key: string;
    el: HTMLElement;
    mesh: THREE.Mesh;
    texture: THREE.Texture;
    scaleMultiplier: number;
}

export class HeroSlider extends ShaderPlaneGroup {
    protected getMeshes() {
        return this.planes.map(p => p.mesh);
    }

    private planes: HeroPlane[] = [];
    private scene: THREE.Scene;
    private root: HTMLElement;
    private activeKey: string = "";
    private initRequestId = 0;
    private markReady: () => void = () => {};

    ready: Promise<void> = Promise.resolve();

    private onSlideChange = (e: Event) => {
        const { to } = (e as CustomEvent<{ to: string }>).detail;
        this.crossfadeTo(to);
    };

    private bindSignal(): void {
        window.addEventListener("hero:slide-change", this.onSlideChange);
    }

    private playExpansion(plane: HeroPlane, introDelay: number): void {
        const mat = plane.mesh.material as THREE.ShaderMaterial;

        const { rest, intro } = tvShader;

        mat.uniforms.uSquircleProgress.value = intro.squircle;
        mat.uniforms.uLensDistortion.value = intro.lens;
        mat.uniforms.uEdgeBump.value = intro.edge + 0.5;

        gsap.to(mat.uniforms.uSquircleProgress, { value: rest.squircle, duration: intro.heroDuration, delay: introDelay, ease: "power2.inOut" });
        gsap.to(mat.uniforms.uLensDistortion, { value: rest.lens, duration: intro.heroDuration - 0.2, delay: introDelay, ease: "power3.out" });
        gsap.to(mat.uniforms.uFocusFactor, { value: rest.focus, duration: 1.2, delay: introDelay, ease: "power3.out" });
        gsap.to(mat.uniforms.uEdgeBump, { value: rest.edge, duration: intro.heroDuration, delay: introDelay + 0.2, ease: "power3.out" });
        gsap.to(plane, { scaleMultiplier: 1, duration: 1, delay: introDelay, ease: "power3.out" });
    }

    replayIntro(): void {
        this.planes.forEach(plane => {
            this.killUniformTweens(plane.mesh.material as THREE.ShaderMaterial);
            (plane.mesh.material as THREE.ShaderMaterial).uniforms.uOpacity.value = plane.key === this.activeKey ? 1 : 0;
            this.playExpansion(plane, 0);
        });
    }

    constructor(scene: THREE.Scene, root: HTMLElement) {
        super();
        this.scene = scene;
        this.root = root;
    }

    init(introDelay: number = 1, introGate: Promise<void> = Promise.resolve()): void {
        this.dispose();
        const requestId = ++this.initRequestId;
        this.ready = new Promise<void>(resolve => (this.markReady = resolve));

        const slides = [...this.root.querySelectorAll<HTMLElement>(".hero [data-hero-preview]")];

        Promise.all(slides.map(el => this.createPlaneFor(el))).then(results => {
            const planes = results.filter((p): p is HeroPlane => p !== null);

            if (requestId !== this.initRequestId) {
                planes.forEach(p => this.destroyPlane(p));
                return;
            }

            this.planes = planes;
            this.applyActiveFromDom();
            this.bindSignal();
            this.waitForFirstFrame(this.planes.find(p => p.key === this.activeKey));

            void Promise.all([loaderDone, introGate]).then(() => {
                if (requestId !== this.initRequestId) return;

                this.planes.forEach(plane => this.playExpansion(plane, introDelay));
            });
        });
    }

    private applyActiveFromDom(): void {
        const activeSlide = this.root.querySelector<HTMLElement>(".hero__bg-slide.swiper-slide-active");
        const domKey = activeSlide?.dataset.heroPreview;
        const activePlane = this.planes.find(p => p.key === domKey) ?? this.planes[0];
        if (!activePlane) return;

        this.activeKey = activePlane.key;

        this.planes.forEach(({ key, mesh }) => {
            const active = key === this.activeKey;
            (mesh.material as THREE.ShaderMaterial).uniforms.uOpacity.value = active ? 1 : 0;
            mesh.visible = active;
        });
    }

    private waitForFirstFrame(plane: HeroPlane | undefined): void {
        const video = plane?.el.querySelector<HTMLVideoElement>("video");
        const signal = () => {
            this.markReady();
            window.dispatchEvent(new Event("hero:first-frame"));
        };

        if (!video || video.readyState >= 2) {
            signal();
            return;
        }

        video.addEventListener("loadeddata", signal, { once: true });
        video.addEventListener("error", signal, { once: true });
    }

    private async createPlaneFor(el: HTMLElement): Promise<HeroPlane | null> {
        const key = el.dataset.heroPreview!;
        const result = await createTextureFromWrapperAsync(el, { autoplay: false });
        if (!result) return null;
        if (!document.contains(el)) return null;

        const meshAspect = el.offsetWidth / el.offsetHeight;
        const geo = new THREE.PlaneGeometry(1, 1, 1, 1);
        const mat = createShaderMaterial(result.texture, meshAspect, result.aspect ? result.aspect : meshAspect);
        mat.uniforms.uOpacity.value = 0;
        mat.uniforms.uSquircleProgress.value = tvShader.intro.squircle;
        mat.uniforms.uLensDistortion.value = tvShader.intro.lens;
        mat.uniforms.uFocusFactor.value = 0.5;
        mat.uniforms.uSharpCorners.value = 1;
        mat.uniforms.uEdgeBump.value = tvShader.intro.edge + 0.5;
        mat.uniforms.uCurveStrength.value = 0;

        const mesh = new THREE.Mesh(geo, mat);
        this.scene.add(mesh);

        const plane: HeroPlane = {
            key,
            el,
            mesh,
            texture: result.texture,
            scaleMultiplier: 0.5,
        };

        return plane;
    }

    crossfadeTo(key: string): void {
        if (key === this.activeKey) return;

        const target = this.planes.find(p => p.key === key);
        if (!target) return;

        const previous = this.planes.find(p => p.key === this.activeKey);
        const channelMaterials = [target.mesh.material as THREE.ShaderMaterial];
        if (previous) channelMaterials.push(previous.mesh.material as THREE.ShaderMaterial);

        if (!prefersReducedMotion) {
            channelMaterials.forEach(mat => {
                gsap.to(mat.uniforms.uChromaticAberration, {
                    keyframes: [
                        { value: tvShader.crossfade.chromatic, duration: 0.16, ease: "power2.out" },
                        { value: 0.5, duration: 0.3, ease: "expo.out" },
                    ],
                    overwrite: true,
                });
                gsap.to(mat.uniforms.uEdgeBump, {
                    keyframes: [
                        { value: tvShader.crossfade.edge, duration: 0.45, ease: "power2.out" },
                        { value: 0, duration: 1.6, ease: "power2.out" },
                    ],
                    overwrite: true,
                });
                gsap.to(mat.uniforms.uRoll, {
                    keyframes: [
                        { value: tvShader.crossfade.roll, duration: 0.16, ease: "power2.out" },
                        { value: 0, duration: 0.3, ease: "expo.out" },
                    ],
                    overwrite: true,
                });
            });
        }

        this.activeKey = key;

        this.planes.forEach(({ key: k, mesh }) => {
            const mat = mesh.material as THREE.ShaderMaterial;

            if (k === key) {
                mesh.visible = true;
                gsap.to(mat.uniforms.uOpacity, { value: 1, duration: 0.35, ease: "power2.inOut", overwrite: true });
            } else {
                gsap.to(mat.uniforms.uOpacity, { value: 0, duration: 0.1, delay: 0.3, ease: "power1.out", overwrite: true });
            }
        });
    }

    syncAll(camera: THREE.PerspectiveCamera, rendererWidth: number, rendererHeight: number, time: number, scrollVelocity: number): void {
        for (const plane of this.planes) {
            const { el, mesh, scaleMultiplier } = plane;
            const mat = mesh.material as THREE.ShaderMaterial;

            mesh.visible = plane.key === this.activeKey || mat.uniforms.uOpacity.value > 0.001;
            if (!mesh.visible) continue;

            const rect = el.getBoundingClientRect();
            mat.uniforms.uParallax.value = (rendererHeight - rect.top) / (rendererHeight + rect.height);
            const { x, y, width, height } = domRectToWorld(rect, camera, rendererWidth, rendererHeight);
            const z = plane.key === this.activeKey ? 0.01 : 0;
            mesh.position.set(x, y, z);
            mesh.scale.set(width * scaleMultiplier, height * scaleMultiplier, 1);

            mat.uniforms.uTime.value = time;
            mat.uniforms.uScrollVelocity.value = scrollVelocity;
            syncCursorWarp(mat, rect);

            const upDrive = prefersReducedMotion ? 0 : THREE.MathUtils.clamp(-scrollVelocity / 2, 0, 1);
            const upLip = upDrive * tvShader.scroll.heroUp;
            mat.uniforms.uScrollLip.value = THREE.MathUtils.lerp(mat.uniforms.uScrollLip.value, upLip, 0.12);
            if (Math.abs(mat.uniforms.uScrollLip.value - upLip) < 0.001) mat.uniforms.uScrollLip.value = upLip;
        }
    }

    private destroyPlane(plane: HeroPlane): void {
        this.killUniformTweens(plane.mesh.material as THREE.ShaderMaterial);
        gsap.killTweensOf(plane);
        this.scene.remove(plane.mesh);
        plane.texture.dispose();
        (plane.mesh.material as THREE.Material).dispose();
        plane.mesh.geometry.dispose();
    }

    dispose(): void {
        this.initRequestId++;
        this.markReady();
        window.removeEventListener("hero:slide-change", this.onSlideChange);
        this.planes.forEach(plane => this.destroyPlane(plane));
        this.planes = [];
    }
}
